import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import RefillCheckoutModal from '../../components/modals/RefillCheckoutModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import * as orderService from '../../services/orderService';
import { isTrtOrder } from '../../utils/trtProducts';
import { ASYNC_REVIEW_NOTICE, APPROVAL_UPDATE_NOTICE } from '../../utils/asyncConsultCopy';
import styles from './OrdersPage.module.css';

const DUMMY_ORDERS = [
  {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    orderNumber: 'OV-10001',
    items: [{ productName: 'Semaglutide', product: { slug: 'semaglutide' } }],
    createdAt: '2026-02-10T14:30:00.000Z',
    status: 'delivered',
    pricing: { formattedTotal: '$299.00' }
  },
  {
    id: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
    orderNumber: 'OV-10002',
    items: [{ productName: 'Testosterone Cypionate', product: { slug: 'testosterone-cypionate' } }],
    createdAt: '2026-02-08T09:15:00.000Z',
    status: 'shipped',
    pricing: { formattedTotal: '$189.00' }
  },
  {
    id: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
    orderNumber: 'OV-10003',
    items: [{ productName: 'Sildenafil', product: { slug: 'sildenafil' } }],
    createdAt: '2026-02-06T16:45:00.000Z',
    status: 'processing',
    pricing: { formattedTotal: '$79.00' }
  },
  {
    id: 'd4e5f6a7-b8c9-0123-def1-234567890123',
    orderNumber: 'OV-10004',
    items: [{ productName: 'Tadalafil', product: { slug: 'tadalafil' } }],
    createdAt: '2026-02-04T11:20:00.000Z',
    status: 'pending',
    pricing: { formattedTotal: '$99.00' }
  },
  {
    id: 'e5f6a7b8-c9d0-1234-ef12-345678901234',
    orderNumber: 'OV-10005',
    items: [{ productName: 'Tirzepatide', product: { slug: 'tirzepatide' } }],
    createdAt: '2026-02-02T08:00:00.000Z',
    status: 'under_review',
    pricing: { formattedTotal: '$449.00' }
  }
];

// Customer-facing TRT lifecycle wording. The labels themselves are authored once
// in `api/services/trtStageService.js` TRT_STATES and arrive already
// customer-readable on `lifecycleStages.trt.label` — they are never re-derived
// here and the raw enum is never rendered.
//
// ⚠️ `needsOperationsAttention` is ops-only (trtStageService marks it `opsOnly`).
// It means a vendor signal was unrecognised or is unconfirmed — that is a fact
// about our integration, NOT a clinical fact about the patient — so it degrades
// to the neutral string rather than being shown.
//
// The same neutral string covers "no TRT state recorded yet", which is the state
// of every order today: nothing calls `recordTrtStateEvent` until tasks 1255/1256
// wire it. Rendering defensively here is what task 1258 asks for.
const TRT_FALLBACK_LABEL = 'In progress';

const getTrtStageLabel = (order) => {
  const trt = order?.lifecycleStages?.trt;
  if (!trt || trt.needsOperationsAttention) return TRT_FALLBACK_LABEL;
  return trt.label ?? TRT_FALLBACK_LABEL;
};

// Card 40755 — the statuses that mean "a doctor has the intake and has not decided yet".
// `getStatusBadge` below stays the single source of the status NAME; this is the async
// EXPLANATION rendered beside it, and its sentences are imported from
// `utils/asyncConsultCopy` so the order list, the dashboard, checkout and the order emails
// cannot drift apart the way they did before that module existed.
const ASYNC_REVIEW_STATUSES = ['pending', 'under_review'];

const getAsyncStatusHint = (status) =>
  ASYNC_REVIEW_STATUSES.includes(status)
    ? `${ASYNC_REVIEW_NOTICE} ${APPROVAL_UPDATE_NOTICE}`
    : null;

const OrdersPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [supportModal, setSupportModal] = useState({ open: false, order: null });
  const [cancelModal, setCancelModal] = useState({ open: false, order: null });
  const [refillDecisionModal, setRefillDecisionModal] = useState({ open: false, order: null });
  const [refillCheckoutModal, setRefillCheckoutModal] = useState({ open: false, order: null, appointment: null });
  const [refillRouting, setRefillRouting] = useState(false);
  const [higherDosageModal, setHigherDosageModal] = useState({ open: false, order: null });
  const [supportMessage, setSupportMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Card 54181: the search is resolved SERVER-SIDE over the member's whole order
  // history, so it has to be a request rather than a filter over the page already
  // in hand. Debounced so typing is not one request per keystroke, and always
  // reset to page 1 because a new term makes the previous page number meaningless.
  useEffect(() => {
    const timer = setTimeout(() => loadOrders(1, searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadOrders = async (page = 1, term = '') => {
    try {
      const search = term.trim();
      const response = await orderService.getMyOrders({
        page,
        limit: 10,
        ...(search ? { search } : {})
      });
      const fetchedOrders = response.orders || [];
      // Card 54181: the demo placeholder stands in for an EMPTY HISTORY, so it must
      // not fire on a search that merely matched nothing -- that is a correct empty
      // result, and "No orders match your search." is the message that belongs to it.
      const isAdminDemo = user?.email === 'admin@ovalcare.com' && !search;
      setOrders(fetchedOrders.length === 0 && isAdminDemo ? DUMMY_ORDERS : fetchedOrders);
      setPagination(response.pagination);
      setLoadError(false);
    } catch (error) {
      console.error('Failed to load orders:', error);
      const isAdminDemo = user?.email === 'admin@ovalcare.com';
      // Card 53601: a failed read is NOT an empty order history. Without this the
      // catch wrote no state at all, so `orders` stayed [] and the table below
      // rendered "No orders found." to a member who actually holds orders.
      if (isAdminDemo) setOrders(DUMMY_ORDERS);
      else setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: '#f59e0b', bg: '#fef3c7' },
      under_review: { label: 'Under Review', color: '#3b82f6', bg: '#dbeafe' },
      approved: { label: 'Approved', color: '#10b981', bg: '#d1fae5' },
      denied: { label: 'Denied', color: '#ef4444', bg: '#fee2e2' },
      refill: { label: 'Refill', color: '#7c3aed', bg: '#f3e8ff' },
      processing: { label: 'Processing', color: '#8b5cf6', bg: '#ede9fe' },
      shipped: { label: 'Shipped', color: '#06b6d4', bg: '#cffafe' },
      delivered: { label: 'Delivered', color: '#22c55e', bg: '#dcfce7' },
      cancelled: { label: 'Cancellation Requested', color: '#6b7280', bg: '#f3f4f6' }
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const handleSubmitSupportTicket = async () => {
    if (supportMessage.length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      await orderService.submitSupportTicket(supportModal.order.id, supportMessage);
      toast.success('Support ticket submitted');
      setSupportModal({ open: false, order: null });
      setSupportMessage('');
    } catch (error) {
      toast.error(error.message || 'Failed to submit support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancel = async () => {
    setSubmitting(true);
    try {
      await orderService.submitCancelRequest(cancelModal.order.id);
      toast.success('Your cancellation request has been submitted. If your request is approved, we will notify you via email.');
      setCancelModal({ open: false, order: null });
    } catch (error) {
      toast.error(error.message || 'Failed to submit cancellation request');
    } finally {
      setSubmitting(false);
    }
  };

  const getProductSlug = (order) =>
    order.items?.[0]?.product?.slug || null;

  // Card 6826 — route the same-dosage refill on the SERVER's decision.
  //
  // `calculateRefill` publishes `schedulingRequired`, true for confirmed-DrCare orders only.
  // We branch on that field and ⛔ never on the product name: `products.provider_type` is a
  // mutable catalog column that retroactively relabels history (90 orders mislabelled, OV-9385),
  // and re-deriving a provider in the browser would be a second copy of a decision that is
  // authored once on the server — the drift card 6827 exists to prevent.
  //
  // On a probe failure we fall through to the modal rather than blocking the refill. The modal
  // re-runs the same call, surfaces the real error, and its own guard keeps Pay disabled if the
  // order does turn out to need a slot.
  const handleRefillSameDosage = async () => {
    const { order } = refillDecisionModal;
    setRefillRouting(true);
    try {
      const response = await orderService.calculateRefill(order.id);
      setRefillDecisionModal({ open: false, order: null });
      if (response?.refill?.schedulingRequired) {
        navigate('/calendar', { state: { refillOrder: order } });
        return;
      }
      setRefillCheckoutModal({ open: true, order, appointment: null });
    } catch {
      setRefillDecisionModal({ open: false, order: null });
      setRefillCheckoutModal({ open: true, order, appointment: null });
    } finally {
      setRefillRouting(false);
    }
  };

  // Returning from the shared calendar. With an appointment the checkout reopens carrying the
  // slot; without one (Back) the user lands back on the dosage-decision step, so the order is
  // never lost and a Pay button that would 400 is never reachable.
  //
  // The state is consumed with `replace`, so a refresh does not reopen a stale modal.
  useEffect(() => {
    const returned = location.state?.refillOrder;
    if (!returned) return;
    const appointment = location.state?.refillAppointment ?? null;
    if (appointment) {
      setRefillCheckoutModal({ open: true, order: returned, appointment });
    } else {
      setRefillDecisionModal({ open: true, order: returned });
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const handleScheduleAppointment = () => {
    const { order } = refillCheckoutModal;
    if (!order) return;
    setRefillCheckoutModal({ open: false, order: null, appointment: null });
    navigate('/calendar', { state: { refillOrder: order } });
  };

  const handleRefillHigherDosage = () => {
    const { order } = refillDecisionModal;
    setRefillDecisionModal({ open: false, order: null });
    setHigherDosageModal({ open: true, order });
  };

  const handleConfirmHigherDosage = () => {
    const { order } = higherDosageModal;
    const slug = getProductSlug(order);
    setHigherDosageModal({ open: false, order: null });
    if (!slug) return;
    sessionStorage.setItem('oval_higher_dosage_request', JSON.stringify({
      higherDosageRequest: true,
      originalOrderId: order.id,
      originalOrderNumber: order.orderNumber || null,
      productSlug: slug,
      originalProduct: order.items?.[0]?.product?.name || order.items?.[0]?.productName || null,
      originalDosage: order.items?.[0]?.dosage || null,
      createdAt: new Date().toISOString()
    }));
    navigate(`/intake/${slug}?higherDosage=1`);
  };

  if (loading) return <LoadingSpinner fullScreen />;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Medication Orders</h1>
        <p>View your order history for past purchases and refills.</p>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.searchBox}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        <div className={styles.tableScroll}>
          <div className={styles.table}>
            <div className={styles.tableHead}>
              <span>Order</span>
              <span>Medication</span>
              <span>Date</span>
              <span>Status</span>
              <span>Total</span>
              <span>Actions</span>
            </div>

            <div className={styles.tableBody}>
              {loadError ? (
                /* Card 53601: the refusal branch, ordered BEFORE the length test so a
                   failed load can never fall through to the empty state. */
                <div className={styles.emptyRow} role="alert">
                  <p>Could not load your orders.</p>
                  <button className={styles.pageBtn} onClick={() => loadOrders(1, searchQuery)}>Try again</button>
                </div>
              ) : orders.length === 0 ? (
                <div className={styles.emptyRow}>
                  {searchQuery ? 'No orders match your search.' : 'No orders found.'}
                </div>
              ) : (
                orders.map((order) => {
                  const status = getStatusBadge(order.status);
                  const slug = getProductSlug(order);
                  const isTrt = isTrtOrder(order);
                  return (
                    <div key={order.id} className={styles.tableRow}>
                      <span className={styles.orderNumber}>{order.orderNumber}</span>
                      <span className={styles.medication}>
                        {order.items?.[0]?.product?.name || order.items?.[0]?.productName || '-'}
                        {order.items?.length > 1 && ` +${order.items.length - 1} more`}
                      </span>
                      <span className={styles.date}>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </span>
                      <span>
                        <span className={styles.statusBadge} style={{ color: status.color, backgroundColor: status.bg }}>
                          {status.label}
                        </span>
                        {isTrt && (
                          <span className={styles.trtStage}>{getTrtStageLabel(order)}</span>
                        )}
                      </span>
                      <span className={styles.total}>{order.pricing?.formattedTotal || '-'}</span>
                      <span className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => { setSupportModal({ open: true, order }); setSupportMessage(''); }}
                        >
                          Support
                        </button>
                        <button
                          className={`${styles.actionBtn} ${styles.cancelBtn}`}
                          onClick={() => setCancelModal({ open: true, order })}
                        >
                          Request Cancellation
                        </button>
                        {slug && (
                          <button
                            className={`${styles.actionBtn} ${styles.refillBtn}`}
                            onClick={() => setRefillDecisionModal({ open: true, order })}
                          >
                            Refill
                          </button>
                        )}
                      </span>
                      {getAsyncStatusHint(order.status) && (
                        <span className={styles.statusHint}>
                          {getAsyncStatusHint(order.status)}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <button disabled={!pagination.hasPrevPage} onClick={() => loadOrders(pagination.page - 1, searchQuery)} className={styles.pageBtn}>
            Previous
          </button>
          <span className={styles.pageInfo}>Page {pagination.page} of {pagination.totalPages}</span>
          <button disabled={!pagination.hasNextPage} onClick={() => loadOrders(pagination.page + 1, searchQuery)} className={styles.pageBtn}>
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={supportModal.open}
        onClose={() => setSupportModal({ open: false, order: null })}
        title={`Support Ticket — ${supportModal.order?.orderNumber || ''}`}
      >
        <div className={styles.modalContent}>
          <p className={styles.modalLabel}>Describe your issue:</p>
          <textarea
            className={styles.modalTextarea}
            value={supportMessage}
            onChange={(e) => setSupportMessage(e.target.value)}
            placeholder="Please describe your issue in detail (min 10 characters)..."
            rows={5}
          />
          <div className={styles.modalActions}>
            <button className={styles.modalSecondaryBtn} onClick={() => setSupportModal({ open: false, order: null })}>
              Close
            </button>
            <button
              className={styles.modalPrimaryBtn}
              onClick={handleSubmitSupportTicket}
              disabled={submitting || supportMessage.length < 10}
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={cancelModal.open}
        onClose={() => setCancelModal({ open: false, order: null })}
        title="Request Cancellation"
      >
        <div className={styles.modalContent}>
          <p className={styles.modalLabel}>
            Are you sure you want to request cancellation for order <strong>{cancelModal.order?.orderNumber}</strong>? Your request will be reviewed, and if approved, you will be notified via email.
          </p>
          <div className={styles.modalActions}>
            <button className={styles.modalSecondaryBtn} onClick={() => setCancelModal({ open: false, order: null })}>
              No, Go Back
            </button>
            <button
              className={`${styles.modalPrimaryBtn} ${styles.modalDangerBtn}`}
              onClick={handleConfirmCancel}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Yes, Request Cancellation'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={refillDecisionModal.open}
        onClose={() => setRefillDecisionModal({ open: false, order: null })}
        title={`Refill — ${refillDecisionModal.order?.orderNumber || ''}`}
      >
        <div className={styles.modalContent}>
          <p className={styles.modalLabel}>
            Would you like this refill for the same dosage or a higher dosage?
          </p>
          <div className={styles.decisionOptions}>
            <button
              className={styles.decisionBtn}
              onClick={handleRefillSameDosage}
              disabled={refillRouting}
            >
              <span className={styles.decisionTitle}>
                {refillRouting ? 'Checking...' : 'Same dosage refill'}
              </span>
              <span className={styles.decisionDesc}>
                Reorder your current dosage — quick checkout, no medical review needed.
              </span>
            </button>
            <button className={styles.decisionBtn} onClick={handleRefillHigherDosage}>
              <span className={styles.decisionTitle}>Higher dosage / increased strength</span>
              <span className={styles.decisionDesc}>
                Request an increased dosage — requires a short medical intake review.
              </span>
            </button>
          </div>
          <div className={styles.modalActions}>
            <button
              className={styles.modalSecondaryBtn}
              onClick={() => setRefillDecisionModal({ open: false, order: null })}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={higherDosageModal.open}
        onClose={() => setHigherDosageModal({ open: false, order: null })}
        title="Higher Dosage Request"
      >
        <div className={styles.modalContent}>
          <p className={styles.modalLabel}>
            You are requesting a higher dosage or increased strength of your medication. This requires a new medical consultation and approval from a licensed provider.
          </p>
          <p className={styles.modalLabel}>
            On the next page, you will complete a new intake request. At checkout, an increased strength fee may be added above the price of your previous order. This additional amount is held or charged because a higher-strength prescription requires additional review and fulfillment.
          </p>
          <p className={styles.modalLabel}>
            If your provider does not approve the increased strength, the increased strength fee will be reversed or refunded according to the payment status, and you may continue with the same dosage or follow the provider's recommendation.
          </p>
          <div className={styles.modalActions}>
            <button
              className={styles.modalSecondaryBtn}
              onClick={() => setHigherDosageModal({ open: false, order: null })}
            >
              Close
            </button>
            <button
              className={`${styles.modalPrimaryBtn} ${styles.modalOrangeBtn}`}
              onClick={handleConfirmHigherDosage}
            >
              I understand
            </button>
          </div>
        </div>
      </Modal>

      <RefillCheckoutModal
        isOpen={refillCheckoutModal.open}
        order={refillCheckoutModal.order}
        appointment={refillCheckoutModal.appointment}
        onScheduleAppointment={handleScheduleAppointment}
        onClose={() => setRefillCheckoutModal({ open: false, order: null, appointment: null })}
      />
    </div>
  );
};

export default OrdersPage;
